import {
  AutoCompletionResponse,
  Prediction,
  predictionJsonToPrediction,
  SearchResultResponse, SearchResultResponseJson,
  searchResultResponseJsonToSearchResultResponse,
  Scope,
  ScopeResponse
} from "@local/models"

/**
 * SearchService
 *
 * Fronts the [search api](https://github.com/CareRevolutions/search_api).
 * It expects the following endpoints:
 * - /tools/search/general - For a full search
 * - /tools/search/autocomplete - For a name search
 */
export class SearchService {

  /**
   * The authentication token to use
   * @type {string}
   */
  authToken: string = null

  /**
   * Fetch available scopes
   */
  getScopes = async (): Promise<Array<Scope>> => {
    const query = `${process.env.PLATFORM_API}/scopes`
    try {
      const response: ScopeResponse = await this.makeFetchCall(query)
      return response.scopes
    } catch (e) {
      throw e
    }
  }


  /**
   * Runs a name-only search
   * @type {Promise<Array<Prediction>>}
   */
  predict = async (
    searchTerm: string = '',
    scopes: Array<Scope> = [],
    from: number = 0,
    size: number = 10
  ): Promise<Array<Prediction>> => {
    const query = `${process.env.PLATFORM_API}/autocompletion/predictions?q=${searchTerm}&scopes=${scopes.join(',')}&from=${from}&size=${size}`
    try {
      const predictions: AutoCompletionResponse = await this.makeFetchCall(query)
      return predictions.map(predictionJsonToPrediction)
    } catch (e) {
      throw e
    }

  }

  /**
   * Runs a full search
   * @param searchTerm
   * @type {Promise<SearchResultResponse>}
   */
  search = async (
    searchTerm: string,
    scopes: Array<Scope> = [],
    from: number = 0,
    size: number = 10
  ): Promise<SearchResultResponse> => {
    try {
      const query = `${process.env.PLATFORM_API}/search/general?q=${searchTerm}&scopes=${scopes.join(',')}&from=${from}&size=${size}`
      const searchResult: SearchResultResponseJson = await this.makeFetchCall(query)
      return searchResultResponseJsonToSearchResultResponse(searchResult)
    } catch (e) {
      throw e
    }

  }

  /**
   * @type AbortController
   * @remarks
   * In a single context, a search service must never make multiple calls. If a new call is started, terminate the existing one first.
   */
  private fetchController: AbortController = new AbortController()

  /**
   * Reset any current fetch calls, and return a signal to dispatch with the next call
   * @type {AbortSignal}
   */
  private resetFetch: Function = (): AbortSignal => {
    this.fetchController.abort()
    delete this.fetchController
    this.fetchController = new AbortController()
    return this.fetchController.signal
  }

  /**
   * Given a query, make a fetch call, and try to return a response.
   * @type {Promise<any>}
   */
  private makeFetchCall: Function = async (query): Promise<any> => {
    try {
      let authToken = ''
      const cookie = decodeURI(document.cookie).split('; ').map(cookie => cookie.split('=')).filter(c => c[0] === '_tools_app_tokens')
      if (cookie.length === 1) {
        authToken =  JSON.parse(decodeURIComponent(cookie[0][1])).access_token
      }
      const options = {}
      options['signal'] = this.resetFetch()
      if (authToken) {
        options['headers'] = {
          Authorization: `Bearer ${authToken}`
        }
      }
      const response: Response = await fetch(query, options)
      const formattedResponse = await response.json()
      if (response.ok) {
        return formattedResponse
      } else {
        throw formattedResponse
      }
    } catch (e: any) {
      throw e
    }
  }

}
